/**
 * App mode store: detects when clients/web is running as an SBAA
 * on a subdomain like {slug}.app.readyloop.ai and loads app-specific config.
 *
 * When isAppMode is true, the UI shows a simplified experience:
 * - Single "Chat" nav item instead of Build/Agents/Marketplace/Governance
 * - Custom app name, logo, and CSS variables
 * - Settings shows only Billing + About
 */

import { create } from 'zustand';

import { apiUrl } from '@/lib/api-base';

export interface WelcomeCard {
  /** Tabler icon name (e.g. "IconBolt") or an emoji / short text. */
  icon: string;
  /** Short label shown on the card. */
  label: string;
  /** Prompt auto-sent as the first user message when clicked. */
  prompt: string;
}

export interface SbaaConfig {
  app_key: string;
  source_agent: string;
  /** e.g. "user/my-skill" — used for voice skillDepends */
  source_skill: string | null;
  display_name: string;
  description: string;
  app_tagline: string;
  app_icon_url: string | null;
  custom_css_vars: Record<string, string> | null;
  welcome_config: {
    cards?: WelcomeCard[];
  } | null;
  /** Set when the backing skill's SKILL.md has `voice: false`.
   *  Hides the Voice sidebar/tab entry. */
  voice_disabled?: boolean;
  frontend_mode: string;
  google_oauth_client_id: string;
  stripe_publishable_key: string;
}

interface AppModeState {
  /** Whether we're in SBAA app mode. */
  isAppMode: boolean;
  /** The app slug extracted from the subdomain. */
  slug: string | null;
  /** Config fetched from /api/v1/sbaa/resolve. */
  appConfig: SbaaConfig | null;
  /** True while initial config fetch is in progress. */
  loading: boolean;
  /** Error message if config fetch fails. */
  error: string | null;
  /** Run once on app start to detect app mode and fetch config. */
  initialize: () => Promise<void>;
}

/** Hostname suffixes that indicate we're serving an SBAA. */
const APP_HOSTNAME_SUFFIXES = ['.app.readyloop.ai', '.app.readyloop-staging.dev'];

function detectAppMode(): { isAppMode: boolean; slug: string | null } {
  // Allow local dev override: make web-server-staging APP=myapp.
  // Dev-only — never honour this in production static builds, otherwise
  // a stray .env.local value leaks into deploy-web.sh's `next build`
  // and every SBAA subdomain resolves the wrong slug.
  if (process.env.NODE_ENV !== 'production') {
    const envSlug = process.env.NEXT_PUBLIC_APP_SLUG;
    if (envSlug) return { isAppMode: true, slug: envSlug };
  }

  if (typeof window === 'undefined') return { isAppMode: false, slug: null };

  const hostname = window.location.hostname;
  for (const suffix of APP_HOSTNAME_SUFFIXES) {
    if (hostname.endsWith(suffix)) {
      const slug = hostname.slice(0, -suffix.length);
      if (slug) return { isAppMode: true, slug };
    }
  }
  return { isAppMode: false, slug: null };
}

// Detect slug eagerly at module load so isAppMode is available
// before the async initialize() completes.
const _initialDetect = detectAppMode();

// Tracks an in-flight initialize() per slug so concurrent callers
// (RootLayout mount + AppLayout pathname effect) share one fetch.
// Without this the second call sees appConfig=null mid-flight,
// issues a parallel fetch, and the resulting double set({appConfig})
// causes downstream useEffects with appConfig in their deps to tear
// down in-flight async work — see chat/page.tsx AutoCreateConversation.
let _inFlight: { slug: string; promise: Promise<void> } | null = null;

export const useAppModeStore = create<AppModeState>()((set, get) => ({
  isAppMode: _initialDetect.isAppMode,
  slug: _initialDetect.slug,
  appConfig: null,
  loading: _initialDetect.isAppMode,
  error: null,

  initialize: async () => {
    const { isAppMode, slug } = detectAppMode();

    if (!isAppMode || !slug) {
      set({ isAppMode: false, slug: null, loading: false });
      return;
    }

    // Already loaded config for this slug — skip redundant fetch.
    const current = get();
    if (current.slug === slug && current.appConfig) {
      return;
    }

    if (_inFlight && _inFlight.slug === slug) {
      return _inFlight.promise;
    }

    set({ isAppMode: true, slug, loading: true, error: null });

    const promise = (async () => {
      try {
        const resp = await fetch(
          apiUrl(`/api/v1/sbaa/resolve?app=${encodeURIComponent(slug)}`),
        );
        if (!resp.ok) {
          const text = await resp.text().catch(() => 'Unknown error');
          set({ isAppMode: false, slug: null, loading: false, error: `App not found: ${text}` });
          return;
        }
        const config: SbaaConfig = await resp.json();
        set({ appConfig: config, loading: false });

        // Inject custom CSS variables into :root
        if (config.custom_css_vars) {
          const root = document.documentElement;
          for (const [key, value] of Object.entries(config.custom_css_vars)) {
            root.style.setProperty(key, value);
          }
        }

        // Update page title
        if (config.display_name) {
          document.title = config.display_name;
        }
      } catch (err) {
        set({
          isAppMode: false,
          slug: null,
          loading: false,
          error: err instanceof Error ? err.message : 'Failed to load app config',
        });
      }
    })();

    _inFlight = { slug, promise };
    try {
      await promise;
    } finally {
      if (_inFlight && _inFlight.promise === promise) _inFlight = null;
    }
  },
}));
